/**
 * useDailyInfraUsage
 *
 * Fetches today's actual send counts for Email and LinkedIn from event tables,
 * instead of relying on stale summary columns on mailboxes/linkedin_profiles.
 *
 * Email:    COUNT of EMAIL_SENT events today from obsync_dynamic_reports
 * LinkedIn: COUNT of message_sent events today from heyreach_linkedin_events
 *
 * Works for both:
 *  - Client users (scoped to their smartleadClientId)
 *  - Admin "All Clients" view (smartleadClientId = undefined → counts across all clients)
 */

import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { utcStartOfDay } from '@/lib/utc-date-utils'

interface DailyInfraUsage {
  emailSentToday: number
  linkedinSentToday: number
}

export function useDailyInfraUsage(
  /** SmartLead numeric client_id (e.g. 167887). undefined = all clients (admin view). */
  smartleadClientId: number | undefined
) {
  return useQuery<DailyInfraUsage>({
    queryKey: ['daily-infra-usage', smartleadClientId ?? 'all'],
    queryFn: async () => {
      const todayUtc = utcStartOfDay(new Date()).toISOString()

      const [emailResult, linkedinResult] = await Promise.all([
        // Email: count today's EMAIL_SENT from obsync_dynamic_reports
        (async () => {
          let query = supabase
            .from('obsync_dynamic_reports')
            .select('id', { count: 'exact', head: true })
            .eq('event_type', 'EMAIL_SENT')
            .gte('created_at', todayUtc)

          if (smartleadClientId) {
            query = query.eq('client_id', smartleadClientId)
          }
          // When undefined (admin "All"), no client filter → counts all clients

          const { count, error } = await query
          if (error) {
            console.error('Error fetching email sent today:', error)
            return 0
          }
          return count || 0
        })(),

        // LinkedIn: count today's message_sent from heyreach_linkedin_events
        (async () => {
          // Get heyreach_ids — scoped to client or all profiles
          let profileQuery = supabase
            .from('linkedin_profiles')
            .select('heyreach_id')
            .not('heyreach_id', 'is', null)

          if (smartleadClientId) {
            profileQuery = profileQuery.eq('client_id', smartleadClientId.toString())
          }
          // When undefined (admin "All"), no filter → all profiles

          const { data: profiles, error: profileError } = await profileQuery
          if (profileError || !profiles?.length) return 0

          const heyreachIds = profiles
            .map(p => Number(p.heyreach_id))
            .filter(id => !isNaN(id))

          if (heyreachIds.length === 0) return 0

          const { count, error } = await (supabase as any)
            .from('heyreach_linkedin_events')
            .select('id', { count: 'exact', head: true })
            .eq('event_type', 'message_sent')
            .in('sender_id', heyreachIds)
            .gte('timestamp', todayUtc)

          if (error) {
            console.error('Error fetching linkedin sent today:', error)
            return 0
          }
          return count || 0
        })(),
      ])

      return {
        emailSentToday: emailResult,
        linkedinSentToday: linkedinResult,
      }
    },
    // Always enabled — runs for both scoped clients and admin "all" view
    staleTime: 2 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  })
}
